//import React from 'react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Progress } from '@/components/ui/progress';
import { Separator } from '@/components/ui/separator';
import { useTheme } from '@/components/ui/theme-provider';
import { 
  Palette, 
  Sun, 
  Moon, 
  Monitor,
  CheckCircle2,
  AlertCircle,
  Info,
  Zap,
  Sparkles,
  Globe
} from 'lucide-react';

export function VercelThemeShowcase() {
  const { theme, accent, setTheme, setAccent } = useTheme();

  const demoCards = [
    {
      title: "Resource Usage",
      description: "Current cloud resource utilization",
      value: "87%",
      progress: 87,
      status: "success",
      icon: CheckCircle2
    },
    {
      title: "Cost Analysis",
      description: "Monthly spend tracking",
      value: "$2,847",
      progress: 65,
      status: "warning",
      icon: AlertCircle
    },
    {
      title: "Performance",
      description: "System performance metrics",
      value: "99.9%",
      progress: 99,
      status: "info",
      icon: Info
    }
  ];

  const accentColors = [
    { id: 'blue', name: 'Vercel Blue', className: 'bg-[var(--vercel-bright-blue)]' },
    { id: 'violet', name: 'Electric Violet', className: 'bg-[var(--vercel-electric-violet)]' },
    { id: 'pink', name: 'Vivid Pink', className: 'bg-[var(--vercel-vivid-pink)]' },
  ] as const;

  return (
    <div className="min-h-screen bg-background p-6 space-y-8">
      {/* Header Section */}
      <div className="space-y-2">
        <div className="flex items-center gap-2">
          <Sparkles className="h-8 w-8 text-primary" />
          <h1 className="text-4xl font-bold text-foreground">
            Vercel Geist Theme Integration
          </h1>
        </div>
        <p className="text-lg text-muted-foreground max-w-2xl">
          Professional cloud management interface with 90% neutral grays and 10% Vercel accent colors. 
          Demonstrating seamless MVPBlocks + Shadcn hybrid architecture theming.
        </p>
      </div>

      {/* Theme Controls */}
      <Card className="border-border">
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Palette className="h-5 w-5" />
            Theme Controls
          </CardTitle>
          <CardDescription>
            Switch between themes and accent colors to see Vercel Geist integration
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-6">
          {/* Theme Mode Selection */}
          <div className="space-y-3">
            <h4 className="text-sm font-medium">Theme Mode</h4>
            <div className="flex gap-2">
              <Button
                variant={theme === 'light' ? 'default' : 'outline'}
                size="sm"
                onClick={() => setTheme('light')}
                className="flex items-center gap-2"
              >
                <Sun className="h-4 w-4" />
                Light
              </Button>
              <Button
                variant={theme === 'dark' ? 'default' : 'outline'}
                size="sm"
                onClick={() => setTheme('dark')}
                className="flex items-center gap-2"
              >
                <Moon className="h-4 w-4" />
                Dark
              </Button>
              <Button
                variant={theme === 'system' ? 'default' : 'outline'}
                size="sm"
                onClick={() => setTheme('system')}
                className="flex items-center gap-2"
              >
                <Monitor className="h-4 w-4" />
                System
              </Button>
            </div>
          </div>

          <Separator />

          {/* Accent Color Selection */}
          <div className="space-y-3">
            <h4 className="text-sm font-medium">Accent Color</h4>
            <div className="flex gap-2">
              {accentColors.map((color) => (
                <Button
                  key={color.id}
                  variant={accent === color.id ? 'default' : 'outline'}
                  size="sm"
                  onClick={() => setAccent(color.id as any)}
                  className="flex items-center gap-2"
                >
                  <div className={`w-3 h-3 rounded-full ${color.className}`} />
                  {color.name}
                </Button>
              ))}
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Color Palette Demonstration */}
      <Card className="border-border">
        <CardHeader>
          <CardTitle>Vercel Color Palette</CardTitle>
          <CardDescription>
            OKLCH-based color system with professional neutral hierarchy
          </CardDescription>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
            {/* Neutral Colors */}
            <div className="space-y-2">
              <h5 className="text-sm font-medium">Neutrals (90%)</h5>
              <div className="space-y-1">
                <div className="h-8 bg-neutral-100 rounded border border-neutral-200 flex items-center justify-center text-xs text-neutral-600">
                  Background
                </div>
                <div className="h-8 bg-neutral-200 rounded flex items-center justify-center text-xs text-neutral-700">
                  Borders
                </div>
                <div className="h-8 bg-neutral-300 rounded flex items-center justify-center text-xs text-neutral-800">
                  Inputs
                </div>
                <div className="h-8 bg-neutral-800 rounded flex items-center justify-center text-xs text-neutral-100">
                  Text
                </div>
              </div>
            </div>

            {/* Vercel Accents */}
            <div className="space-y-2">
              <h5 className="text-sm font-medium">Accents (10%)</h5>
              <div className="space-y-1">
                <div className="h-8 bg-[var(--vercel-bright-blue)] rounded flex items-center justify-center text-xs text-white">
                  Primary
                </div>
                <div className="h-8 bg-[var(--vercel-electric-violet)] rounded flex items-center justify-center text-xs text-white">
                  Secondary
                </div>
                <div className="h-8 bg-[var(--vercel-french-violet)] rounded flex items-center justify-center text-xs text-white">
                  Tertiary
                </div>
                <div className="h-8 bg-[var(--vercel-hot-red)] rounded flex items-center justify-center text-xs text-white">
                  Error
                </div>
              </div>
            </div>

            {/* Interactive States */}
            <div className="space-y-2">
              <h5 className="text-sm font-medium">Interactive</h5>
              <div className="space-y-1">
                <Button size="sm" className="w-full justify-start">
                  Primary Action
                </Button>
                <Button variant="secondary" size="sm" className="w-full justify-start">
                  Secondary
                </Button>
                <Button variant="outline" size="sm" className="w-full justify-start">
                  Outline
                </Button>
                <Button variant="ghost" size="sm" className="w-full justify-start">
                  Ghost
                </Button>
              </div>
            </div>

            {/* Status Indicators */}
            <div className="space-y-2">
              <h5 className="text-sm font-medium">Status</h5>
              <div className="space-y-1">
                <Badge variant="default" className="justify-center w-full">
                  Active
                </Badge>
                <Badge variant="secondary" className="justify-center w-full">
                  Pending
                </Badge>
                <Badge variant="outline" className="justify-center w-full">
                  Inactive
                </Badge>
                <Badge variant="destructive" className="justify-center w-full">
                  Error
                </Badge>
              </div>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Demo Cards Grid */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
        {demoCards.map((card, index) => {
          const Icon = card.icon;
          return (
            <Card key={index} className="border-border hover:shadow-lg transition-all duration-300">
              <CardHeader className="pb-2">
                <div className="flex items-center justify-between">
                  <Icon className={`h-5 w-5 ${
                    card.status === 'success' ? 'text-green-500' :
                    card.status === 'warning' ? 'text-orange-500' :
                    'text-blue-500'
                  }`} />
                  <Badge variant={card.status === 'success' ? 'default' : 'secondary'}>
                    {card.status}
                  </Badge>
                </div>
                <CardTitle className="text-lg">{card.title}</CardTitle>
                <CardDescription>{card.description}</CardDescription>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="text-3xl font-bold text-primary">{card.value}</div>
                <div className="space-y-2">
                  <div className="flex justify-between text-sm">
                    <span className="text-muted-foreground">Progress</span>
                    <span className="font-medium">{card.progress}%</span>
                  </div>
                  <Progress value={card.progress} className="h-2" />
                </div>
              </CardContent>
            </Card>
          );
        })}
      </div>

      {/* Gradient Accent Demo */}
      <Card className="border-border">
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Zap className="h-5 w-5" />
            Vercel Gradient Accents
          </CardTitle>
          <CardDescription>
            Dynamic gradient elements for visual hierarchy and brand consistency
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="h-32 vercel-gradient-accent rounded-lg flex items-center justify-center">
            <div className="text-white font-semibold text-lg text-center">
              <Globe className="h-8 w-8 mx-auto mb-2" />
              Cloud Infrastructure
            </div>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div className="h-20 bg-gradient-to-r from-[var(--vercel-bright-blue)] to-[var(--vercel-electric-violet)] rounded-lg flex items-center justify-center text-white font-medium">
              Horizontal Gradient
            </div>
            <div className="h-20 bg-gradient-to-b from-[var(--vercel-electric-violet)] to-[var(--vercel-french-violet)] rounded-lg flex items-center justify-center text-white font-medium">
              Vertical Gradient
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Typography Demonstration */}
      <Card className="border-border">
        <CardHeader>
          <CardTitle>Vercel Typography System</CardTitle>
          <CardDescription>
            Inter and Inter Tight font families with optimized spacing and contrast
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="space-y-2">
            <h1 className="text-4xl font-bold">Heading 1 - Inter Tight</h1>
            <h2 className="text-3xl font-semibold">Heading 2 - Inter Tight</h2>
            <h3 className="text-2xl font-medium">Heading 3 - Inter Tight</h3>
            <p className="text-base text-foreground">
              Body text using Inter font family with optimized readability and professional appearance. 
              This demonstrates the font smoothing and letter spacing applied across the interface.
            </p>
            <p className="text-sm text-muted-foreground">
              Secondary text in muted color for supporting information and captions.
            </p>
          </div>
        </CardContent>
      </Card>
    </div>
  );
}

export default VercelThemeShowcase;